import { useCallback, useMemo, useState, useEffect, type ChangeEvent } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styles from "./SelectPropertyTypePage.module.css";
import { SignatureModal } from "../signatureModal/SignatureModal.tsx";
import { PropertyStepperScreen } from "@/shared/components/common/PropertyStepperScreen";
import { AddSupportingDocumentsModalPage } from "../addSupportingDocuments/AddSupportingDocumentsModalPage.tsx";
import { useSelectPropertyTypePageViewModal } from "@/features/protest/viewModal/selectPropertyTypeViewmodal";
import type {
  HomesteadSubmissionData,
  PropertyRelationshipSubmission,
} from "@/features/protest/viewModal/signatureViewmodal";
import { useSignatureContinueModal } from "@/features/protest/viewModal/signatureViewmodal";
import { ProtestSuccessModal } from "@/features/protest/view/selectPropertyType/signatureModal/ProtestSuccessModal";
import { Modal } from "@/shared/modal";
import { ProtestStepperContent } from "@/features/protest/view/selectPropertyType/supportingDocumentsStepper/index.ts";
import { Images } from "@/assets/images1";
import { signatureService } from "@/features/protest/services/signature.services";
import { propertySupportService } from "@/features/properties/model/propertyServices";
import { toastService } from "@/shared/toast";
import { tokenService } from "@/services/auth/token.service";
import { ProtestRegistrationModal } from "@/features/protest/view/paymentSuccess/components/ProtestRegistrationModal/ProtestRegistrationModal";

const HomeIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M3 10.5 12 3l9 7.5" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    <path d="M5.5 10.5V21h13V10.5" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    <path d="M10 21v-7h4v7" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
  </svg>
);

const BuildingIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M4 21V7a2 2 0 0 1 2-2h7v16"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path
      d="M13 9h5a2 2 0 0 1 2 2v10H13V9Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path d="M7 9h3M7 12h3M7 15h3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M16 13h1.5M16 16h1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M9 6l6 6-6 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const LandIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M3 19h18M3 15l5-6 4 4 4-5 5 7"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="17" cy="7" r="2" stroke="currentColor" strokeWidth="2" />
  </svg>
);

type RelationshipType = "owner" | "family";
type OwnerRelationship = "Spouse" | "Child" | "Parent" | "Other";

type FamilyFieldErrors = {
  ownerRelationship?: string;
  filingReason?: string;
  drivingLicenseFront?: string;
  drivingLicenseBack?: string;
  previousAddress?: string;
  deedClosing?: string;
  homesteadCategory?: string;
};

const ownerRelationshipOptions: OwnerRelationship[] = ["Spouse", "Child", "Parent", "Other"];

const REQUIRED_FIELD_ERROR = "This field is required";
// const deviceId = getDeviceId();

interface PropertyAddressLike {
  address?: {
    full_address?: string | null;
    line1?: string | null;
    street?: string | null;
    line2?: string | null;
    city?: string | null;
    state?: string | null;
    zip_code?: string | null;
    postal_code?: string | null;
    county?: string | null;
  } | null;
}

const getFormattedAddress = (property?: PropertyAddressLike | null): string => {
  if (property?.address?.full_address) return property.address.full_address;
  const parts = [
    property?.address?.line1 || property?.address?.street,
    property?.address?.line2,
    property?.address?.city,
    property?.address?.state || "TX",
    property?.address?.zip_code || property?.address?.postal_code,
  ].filter(Boolean);
  if (parts.length > 0) return parts.join(", ");
  return "Property Address";
};

const getFormattedCounty = (property?: PropertyAddressLike | null): string => {
  if (property?.address?.county) {
    const c = String(property.address.county)
      .replace(/ county/i, "")
      .trim();
    return `${c} County, ${property?.address?.state || "TX"}`;
  }
  if (property?.address?.city) {
    return `${property.address.city}, ${property?.address?.state || "TX"}`;
  }
  return "Texas";
};

export const SelectPropertyTypePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const vm = useSelectPropertyTypePageViewModal();
  console.log("vm435435dsfsfs", vm);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [isRegistrationModalOpen, setIsRegistrationModalOpen] = useState(false);

  const guestCustomerEmail = useMemo(() => {
    const stateEmail = (location.state as { customerEmail?: string })?.customerEmail;
    if (stateEmail) return stateEmail;
    try {
      return localStorage.getItem("protest_customer_email") || "";
    } catch {
      return "";
    }
  }, [location.state]);

  const [relationshipTypes, setRelationshipTypes] = useState<Record<string, RelationshipType>>({});
  const [ownerRelationships, setOwnerRelationships] = useState<Record<string, OwnerRelationship>>(
    {}
  );
  const [filingReasons, setFilingReasons] = useState<Record<string, string>>({});
  const [familyLicenseFiles, setFamilyLicenseFiles] = useState<Record<string, File>>({});
  const [familyLicenseFileNames, setFamilyLicenseFileNames] = useState<Record<string, string>>({});

  const [drivingLicenseFrontFiles, setDrivingLicenseFrontFiles] = useState<Record<string, File>>(
    {}
  );
  const [drivingLicenseFrontFileNames, setDrivingLicenseFrontFileNames] = useState<
    Record<string, string>
  >({});
  const [drivingLicenseFrontUrls, setDrivingLicenseFrontUrls] = useState<Record<string, string>>(
    {}
  );

  const [drivingLicenseBackFiles, setDrivingLicenseBackFiles] = useState<Record<string, File>>({});
  const [drivingLicenseBackFileNames, setDrivingLicenseBackFileNames] = useState<
    Record<string, string>
  >({});
  const [drivingLicenseBackUrls, setDrivingLicenseBackUrls] = useState<Record<string, string>>({});

  const [deedClosingFiles, setDeedClosingFiles] = useState<Record<string, File>>({});
  const [deedClosingFileNames, setDeedClosingFileNames] = useState<Record<string, string>>({});
  const [deedClosingUrls, setDeedClosingUrls] = useState<Record<string, string>>({});

  const [familyFieldErrors, setFamilyFieldErrors] = useState<Record<string, FamilyFieldErrors>>({});
  const [step4Error, setStep4Error] = useState<string>("");

  const [residentialStatuses, setResidentialStatuses] = useState<
    Record<string, "moving" | "living">
  >({});
  const [occupancyQuestionAnswers, setOccupancyQuestionAnswers] = useState<
    Record<string, "yes" | "no">
  >({});
  const [previousTexasAddresses, setPreviousTexasAddresses] = useState<Record<string, string>>({});
  const [isEditingPreviousAddress, setIsEditingPreviousAddress] = useState<Record<string, boolean>>(
    {}
  );
  const [addressInputValues, setAddressInputValues] = useState<Record<string, string>>({});
  const [addressSuggestions, setAddressSuggestions] = useState<
    Record<
      string,
      Array<{
        address?: string;
        house?: string;
        street?: string;
        city?: string;
        state?: string;
        zip?: string;
      }>
    >
  >({});
  const [isGeneralHomesteads, setIsGeneralHomesteads] = useState<Record<string, boolean>>({});
  const [isOver65s, setIsOver65s] = useState<Record<string, boolean>>({});
  const [fileHomesteadExemption, setFileHomesteadExemption] = useState<Record<string, boolean>>({});
  const [focusedSection, setFocusedSection] = useState<Record<string, string>>({});
  const [selectedForProtest, setSelectedForProtest] = useState<Record<string, boolean>>({});
  const [expandedStep2, setExpandedStep2] = useState<Record<string, boolean>>({});
  const [expandedStep3, setExpandedStep3] = useState<Record<string, boolean>>({});

  const handleToggleGeneralHomestead = useCallback((propertyId: string, checked: boolean) => {
    setIsGeneralHomesteads((prev) => ({ ...prev, [propertyId]: checked }));
    if (checked) {
      setIsOver65s((prev) => ({ ...prev, [propertyId]: false }));
    }
    setFamilyFieldErrors((prev) => ({
      ...prev,
      [propertyId]: { ...prev[propertyId], homesteadCategory: undefined },
    }));
  }, []);

  const handleToggleOver65 = useCallback((propertyId: string, checked: boolean) => {
    setIsOver65s((prev) => ({ ...prev, [propertyId]: checked }));
    if (checked) {
      setIsGeneralHomesteads((prev) => ({ ...prev, [propertyId]: false }));
    }
    setFamilyFieldErrors((prev) => ({
      ...prev,
      [propertyId]: { ...prev[propertyId], homesteadCategory: undefined },
    }));
  }, []);

  const getSectionStatus = useCallback(
    (
      propertyId: string,
      sectionKey: "category" | "occupancy" | "question" | "previousAddress" | "documents"
    ): "active" | "completed" | "faded" => {
      if (focusedSection[propertyId] === sectionKey) {
        return "active";
      }

      const hasCategory = Boolean(isGeneralHomesteads[propertyId] || isOver65s[propertyId]);
      const hasOccupancy = Boolean(residentialStatuses[propertyId]);
      const hasQuestion = Boolean(occupancyQuestionAnswers[propertyId]);

      const resStatus = residentialStatuses[propertyId] || "moving";
      const occupancyAnswer = occupancyQuestionAnswers[propertyId] || "yes";
      const shouldShowConditional =
        (resStatus === "moving" && occupancyAnswer === "yes") ||
        (resStatus === "living" && occupancyAnswer === "no");
      const hasAddress = Boolean(previousTexasAddresses[propertyId]?.trim());

      switch (sectionKey) {
        case "category":
          return hasCategory ? "completed" : "active";
        case "occupancy":
          if (!hasCategory && !focusedSection[propertyId]) return "faded";
          return hasOccupancy ? "completed" : "active";
        case "question":
          if (!hasOccupancy && !focusedSection[propertyId]) return "faded";
          return hasQuestion ? "completed" : "active";
        case "previousAddress":
          if (!hasQuestion && !focusedSection[propertyId]) return "faded";
          return hasAddress ? "completed" : "active";
        case "documents":
          if (!hasQuestion && !focusedSection[propertyId]) return "faded";
          if (shouldShowConditional && !hasAddress && !focusedSection[propertyId]) return "faded";
          return "active";
        default:
          return "active";
      }
    },
    [
      focusedSection,
      isGeneralHomesteads,
      isOver65s,
      residentialStatuses,
      occupancyQuestionAnswers,
      previousTexasAddresses,
    ]
  );

  const getPropertyId = useCallback(
    (
      property?: { property_id?: string; propertyId?: string; id?: string | number } | null
    ): string => {
      if (!property) return vm.selectedPropertyId || "";
      return String(
        property.property_id ?? property.propertyId ?? property.id ?? vm.selectedPropertyId ?? ""
      );
    },
    [vm.selectedPropertyId]
  );

  const displayedProperties = useMemo(() => {
    if (vm.filteredProperties.length > 0) return vm.filteredProperties;
    if (vm.isAllPropertiesLoading) return [];
    return vm.propertyDetails ? [vm.propertyDetails] : [];
  }, [vm.filteredProperties, vm.isAllPropertiesLoading, vm.propertyDetails]);
  console.log(displayedProperties, "displayedProperties4354");

  const isProtesting = useCallback(
    (propertyId: string) => {
      if (selectedForProtest[propertyId] !== undefined) {
        return selectedForProtest[propertyId];
      }
      if (vm.navStatePaidPropertyIds && vm.navStatePaidPropertyIds.length > 0) {
        return vm.navStatePaidPropertyIds.includes(propertyId);
      }
      return true; // default in other flows
    },
    [selectedForProtest, vm.navStatePaidPropertyIds]
  );

  const activeProperties = useMemo(() => {
    return displayedProperties.filter((p) => isProtesting(getPropertyId(p)));
  }, [displayedProperties, isProtesting, getPropertyId]);

  const handleOwnerSelect = useCallback((propertyId: string) => {
    setRelationshipTypes((prev) => ({ ...prev, [propertyId]: "owner" }));
    setFamilyFieldErrors((prev) => ({ ...prev, [propertyId]: {} }));
  }, []);

  const handleFamilySelect = useCallback((propertyId: string) => {
    setRelationshipTypes((prev) => ({ ...prev, [propertyId]: "family" }));
  }, []);

  const handleOwnerRelationshipSelect = useCallback(
    (propertyId: string, value: OwnerRelationship) => {
      setOwnerRelationships((prev) => ({ ...prev, [propertyId]: value }));
      setFamilyFieldErrors((prev) => ({
        ...prev,
        [propertyId]: { ...prev[propertyId], ownerRelationship: undefined },
      }));
    },
    []
  );

  const handleReasonChange = useCallback((propertyId: string, value: string) => {
    setFilingReasons((prev) => ({ ...prev, [propertyId]: value }));
    setFamilyFieldErrors((prev) => ({
      ...prev,
      [propertyId]: { ...prev[propertyId], filingReason: undefined },
    }));
  }, []);

  const handleToggleProtest = useCallback((propertyId: string, checked: boolean) => {
    setSelectedForProtest((prev) => ({ ...prev, [propertyId]: checked }));
  }, []);

  const handleFamilyDrivingLicenseChange = useCallback(
    async (propertyId: string, event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0] ?? null;
      if (file) {
        setFamilyLicenseFiles((prev) => ({ ...prev, [propertyId]: file }));
        setFamilyLicenseFileNames((prev) => ({ ...prev, [propertyId]: file.name }));

        if (tokenService.hasToken()) {
          try {
            await signatureService.uploadKYCFile({
              property_id: propertyId,
              document_type: "driving_licence",
              file: file,
            });
          } catch (error) {
            console.error("Failed to upload family driving license:", error);
          }
        }
      }
      setFamilyFieldErrors((prev) => ({
        ...prev,
        [propertyId]: { ...prev[propertyId], drivingLicenseFront: undefined },
      }));
    },
    []
  );

  const handleDrivingLicenseFrontChange = useCallback(
    async (propertyId: string, event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0] ?? null;
      if (file) {
        setDrivingLicenseFrontFiles((prev) => ({ ...prev, [propertyId]: file }));
        setDrivingLicenseFrontFileNames((prev) => ({ ...prev, [propertyId]: file.name }));
        const previewUrl = URL.createObjectURL(file);
        setDrivingLicenseFrontUrls((prev) => ({ ...prev, [propertyId]: previewUrl }));

        if (tokenService.hasToken()) {
          try {
            await signatureService.uploadHomesteadDocument({
              property_id: propertyId,
              document_type: "driving_licence",
              document_side: "front_side",
              file: file,
            });
          } catch (error) {
            console.error("Failed to upload driving license front:", error);
          }
        }
      }
      setFamilyFieldErrors((prev) => ({
        ...prev,
        [propertyId]: { ...prev[propertyId], drivingLicenseFront: undefined },
      }));
    },
    []
  );

  const handleDrivingLicenseBackChange = useCallback(
    async (propertyId: string, event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0] ?? null;
      if (file) {
        setDrivingLicenseBackFiles((prev) => ({ ...prev, [propertyId]: file }));
        setDrivingLicenseBackFileNames((prev) => ({ ...prev, [propertyId]: file.name }));
        const previewUrl = URL.createObjectURL(file);
        setDrivingLicenseBackUrls((prev) => ({ ...prev, [propertyId]: previewUrl }));

        if (tokenService.hasToken()) {
          try {
            await signatureService.uploadHomesteadDocument({
              property_id: propertyId,
              document_type: "driving_licence",
              document_side: "back_side",
              file: file,
            });
          } catch (error) {
            console.error("Failed to upload driving license back:", error);
          }
        }
      }
      setFamilyFieldErrors((prev) => ({
        ...prev,
        [propertyId]: { ...prev[propertyId], drivingLicenseBack: undefined },
      }));
    },
    []
  );

  const handleDeedClosingChange = useCallback(
    async (propertyId: string, event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0] ?? null;
      if (file) {
        setDeedClosingFiles((prev) => ({ ...prev, [propertyId]: file }));
        setDeedClosingFileNames((prev) => ({ ...prev, [propertyId]: file.name }));
        const previewUrl = URL.createObjectURL(file);
        setDeedClosingUrls((prev) => ({ ...prev, [propertyId]: previewUrl }));

        if (tokenService.hasToken()) {
          try {
            await signatureService.uploadHomesteadDocument({
              property_id: propertyId,
              document_type: "deed_closing_document",
              document_side: "front_side",
              file: file,
            });
          } catch (error) {
            console.error("Failed to upload deed/closing documents:", error);
          }
        }
      }
      setFamilyFieldErrors((prev) => ({
        ...prev,
        [propertyId]: { ...prev[propertyId], deedClosing: undefined },
      }));
    },
    []
  );

  const handleResidentialStatusChange = useCallback(
    (propertyId: string, status: "moving" | "living") => {
      setResidentialStatuses((prev) => ({ ...prev, [propertyId]: status }));
      setFamilyFieldErrors((prev) => ({
        ...prev,
        [propertyId]: {
          ...prev[propertyId],
          previousAddress: undefined,
          deedClosing: undefined,
        },
      }));
    },
    []
  );

  const handleOccupancyQuestionAnswerChange = useCallback(
    (propertyId: string, answer: "yes" | "no") => {
      setOccupancyQuestionAnswers((prev) => ({ ...prev, [propertyId]: answer }));
      setFamilyFieldErrors((prev) => ({
        ...prev,
        [propertyId]: {
          ...prev[propertyId],
          previousAddress: undefined,
          deedClosing: undefined,
        },
      }));
    },
    []
  );

  const handleAddressInputChange = useCallback(async (propertyId: string, value: string) => {
    setAddressInputValues((prev) => ({ ...prev, [propertyId]: value }));
    if (value.trim().length >= 3) {
      try {
        const res = await propertySupportService.getRealStateAutoCompletePropertyDetails({
          search: value.trim(),
          search_types: ["A"],
        });
        if (Array.isArray(res)) {
          setAddressSuggestions((prev) => ({ ...prev, [propertyId]: res.slice(0, 5) }));
        }
      } catch {
        // ignore autocomplete failure
      }
    } else {
      setAddressSuggestions((prev) => ({ ...prev, [propertyId]: [] }));
    }
  }, []);

  const handleSaveAddress = useCallback(
    (propertyId: string, explicitValue?: string) => {
      const val = (
        explicitValue !== undefined
          ? explicitValue
          : (addressInputValues[propertyId] ?? previousTexasAddresses[propertyId] ?? "")
      ).trim();
      if (val) {
        setPreviousTexasAddresses((prev) => ({ ...prev, [propertyId]: val }));
        setIsEditingPreviousAddress((prev) => ({ ...prev, [propertyId]: false }));
        setAddressSuggestions((prev) => ({ ...prev, [propertyId]: [] }));
        setFamilyFieldErrors((prev) => ({
          ...prev,
          [propertyId]: { ...prev[propertyId], previousAddress: undefined },
        }));
      }
    },
    [addressInputValues, previousTexasAddresses]
  );

  const validateFamilyFields = useCallback((): boolean => {
    const errors: Record<string, FamilyFieldErrors> = {};
    let hasError = false;

    for (const p of activeProperties) {
      const pid = getPropertyId(p);
      const type = relationshipTypes[pid] || "owner";
      if (type !== "family") continue;

      const pErrors: FamilyFieldErrors = {};
      if (!ownerRelationships[pid]) {
        pErrors.ownerRelationship = REQUIRED_FIELD_ERROR;
        hasError = true;
      }
      if (!filingReasons[pid]?.trim()) {
        pErrors.filingReason = REQUIRED_FIELD_ERROR;
        hasError = true;
      }
      if (!familyLicenseFiles[pid]) {
        pErrors.drivingLicenseFront = REQUIRED_FIELD_ERROR;
        hasError = true;
      }

      if (Object.keys(pErrors).length > 0) {
        errors[pid] = pErrors;
      }
    }

    setFamilyFieldErrors(errors);
    return !hasError;
  }, [
    activeProperties,
    relationshipTypes,
    ownerRelationships,
    filingReasons,
    familyLicenseFiles,
    getPropertyId,
  ]);

  const validateStep3 = useCallback((): boolean => {
    const errors: Record<string, FamilyFieldErrors> = {};
    let hasError = false;

    for (const p of activeProperties) {
      const pid = getPropertyId(p);
      if (!vm.isSelected(pid, "RESIDENTIAL")) continue;
      if (fileHomesteadExemption[pid] === false) continue;

      const pErrors: FamilyFieldErrors = { ...familyFieldErrors[pid] };

      // Category validation (Note 1)
      if (!isGeneralHomesteads[pid] && !isOver65s[pid]) {
        pErrors.homesteadCategory = "Please select at least one exemption category";
        hasError = true;
      } else {
        pErrors.homesteadCategory = undefined;
      }

      const resStatus = residentialStatuses[pid] || "moving";
      const answer = occupancyQuestionAnswers[pid] || "yes";
      const shouldShowConditional =
        (resStatus === "moving" && answer === "yes") || (resStatus === "living" && answer === "no");

      // Drivers license front and back are NOT mandatory (Note 3)
      pErrors.drivingLicenseFront = undefined;
      pErrors.drivingLicenseBack = undefined;

      if (shouldShowConditional) {
        if (!previousTexasAddresses[pid]?.trim()) {
          pErrors.previousAddress = REQUIRED_FIELD_ERROR;
          hasError = true;
        } else {
          pErrors.previousAddress = undefined;
        }

        // Deed / closing documents are NOT mandatory (Note 3)
        pErrors.deedClosing = undefined;
      } else {
        pErrors.previousAddress = undefined;
        pErrors.deedClosing = undefined;
      }

      if (Object.values(pErrors).some(Boolean)) {
        errors[pid] = pErrors;
      }
    }

    if (hasError) {
      setFamilyFieldErrors((prev) => ({ ...prev, ...errors }));
    } else {
      setFamilyFieldErrors((prev) => {
        const next = { ...prev };
        for (const p of activeProperties) {
          const pid = getPropertyId(p);
          if (next[pid]) {
            next[pid] = {
              ...next[pid],
              homesteadCategory: undefined,
              drivingLicenseFront: undefined,
              drivingLicenseBack: undefined,
              previousAddress: undefined,
              deedClosing: undefined,
            };
          }
        }
        return next;
      });
    }
    return !hasError;
  }, [
    activeProperties,
    vm,
    fileHomesteadExemption,
    isGeneralHomesteads,
    isOver65s,
    residentialStatuses,
    occupancyQuestionAnswers,
    previousTexasAddresses,
    familyFieldErrors,
    getPropertyId,
  ]);

  const homesteadSubmissions = useMemo<Record<string, HomesteadSubmissionData>>(() => {
    const res: Record<string, HomesteadSubmissionData> = {};
    for (const p of activeProperties) {
      const pid = getPropertyId(p);
      if (!vm.isSelected(pid, "RESIDENTIAL")) continue;
      if (fileHomesteadExemption[pid] === false) continue;

      const status = residentialStatuses[pid] || "moving";
      const occupancyLabel =
        status === "moving" ? "Moving into this home" : "Already living in this home";
      const qAnswer = occupancyQuestionAnswers[pid] || "yes";
      const prevAddress = previousTexasAddresses[pid] || "";

      res[pid] = {
        homestead: Boolean(isGeneralHomesteads[pid] || isOver65s[pid]),
        occupancy: occupancyLabel,
        question: qAnswer,
        address: prevAddress,
      };
    }
    return res;
  }, [
    activeProperties,
    vm,
    fileHomesteadExemption,
    residentialStatuses,
    occupancyQuestionAnswers,
    previousTexasAddresses,
    isGeneralHomesteads,
    isOver65s,
    getPropertyId,
  ]);

  const propertyRelationships = useMemo<Record<string, PropertyRelationshipSubmission>>(() => {
    const res: Record<string, PropertyRelationshipSubmission> = {};
    for (const p of activeProperties) {
      const pid = getPropertyId(p);
      const type = relationshipTypes[pid] || "owner";
      if (type !== "family") {
        res[pid] = { type: "owner" };
      } else {
        res[pid] = {
          type: "family",
          relationshipToOwner: ownerRelationships[pid] ?? "",
          reasonForFiling: filingReasons[pid] ?? "",
          drivingLicenseFrontFile: familyLicenseFiles[pid] ?? null,
        };
      }
    }
    return res;
  }, [
    activeProperties,
    relationshipTypes,
    ownerRelationships,
    filingReasons,
    familyLicenseFiles,
    getPropertyId,
  ]);

  const handleSignatureModalSubmit = useCallback(() => {
    // AFTER sign document and bulk APIs call, prompt guest to sign up
    if (!tokenService.hasToken()) {
      setIsRegistrationModalOpen(true);
      return;
    }

    if (activeProperties.length > 1) {
      navigate("/home");
    } else {
      vm.onSignatureModalSubmit();
    }
  }, [activeProperties.length, navigate, vm]);

  const sigVm = useSignatureContinueModal({
    isOpen: currentStep === 4,
    selectedPropertyTypes: vm.selectedPropertyTypes,
    propertyRelationships,
    isGeneralHomesteads,
    isOver65s,
    homesteadSubmissions,
    paidPropertyIds: activeProperties.map((p) => getPropertyId(p)),
    onClose: () => {},
    onSubmit: handleSignatureModalSubmit,
  });

  useEffect(() => {
    if (step4Error && sigVm.signature && sigVm.signatureConsentAccepted) {
      setStep4Error("");
    }
  }, [step4Error, sigVm.signature, sigVm.signatureConsentAccepted]);

  const handleStepNext = useCallback(() => {
    if (activeProperties.length === 0) return;

    if (currentStep === 1) {
      if (!validateFamilyFields()) return;
      setCurrentStep(2);
    } else if (currentStep === 2) {
      setCurrentStep(3);
    } else if (currentStep === 3) {
      if (!validateStep3()) return;
      setCurrentStep(4);
    } else if (currentStep === 4) {
      if (!sigVm.signature && !sigVm.signatureConsentAccepted) {
        toastService.show(
          "Please provide your signature and accept the agreement to continue.",
          "error"
        );
        return;
      } else if (!sigVm.signature) {
        toastService.show("Please provide your signature.", "error");
        return;
      } else if (!sigVm.signatureConsentAccepted) {
        toastService.show("Please accept the agreement.", "error");
        return;
      }
      setStep4Error("");

      // Directly submit sign document and bulk APIs (do NOT ask for signup before)
      void sigVm.handleSubmit();
    }
  }, [currentStep, validateFamilyFields, validateStep3, sigVm, activeProperties.length]);

  const handleRegistrationSuccess = useCallback(() => {
    setIsRegistrationModalOpen(false);
    toastService.show("Account created successfully! Your protest is saved.", "success");
    if (activeProperties.length > 1) {
      navigate("/home");
    } else {
      vm.onSignatureModalSubmit();
    }
  }, [activeProperties.length, navigate, vm]);

  const handleStepBack = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep]);

  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        {/* Top Stepper Header Bar */}
        <div className={styles.stepperHeader}>
          <div
            className={`${styles.stepItem} ${currentStep >= 1 ? styles.stepItemActive : ""}`}
            onClick={() => setCurrentStep(1)}
            role="button"
            tabIndex={0}
          >
            <div
              className={
                currentStep === 1
                  ? styles.stepCircleActive
                  : currentStep > 1
                    ? styles.stepCircleCompleted
                    : styles.stepCircle
              }
            >
              01
            </div>
            <span className={currentStep === 1 ? styles.stepLabelActive : styles.stepLabel}>
              Relationship to Property
            </span>
          </div>
          <div className={`${styles.stepLine} ${currentStep > 1 ? styles.stepLineActive : ""}`} />

          <div
            className={`${styles.stepItem} ${currentStep >= 2 ? styles.stepItemActive : ""}`}
            onClick={() => setCurrentStep(2)}
            role="button"
            tabIndex={0}
          >
            <div
              className={
                currentStep === 2
                  ? styles.stepCircleActive
                  : currentStep > 2
                    ? styles.stepCircleCompleted
                    : styles.stepCircle
              }
            >
              02
            </div>
            <span className={currentStep === 2 ? styles.stepLabelActive : styles.stepLabel}>
              What Kind of Property
            </span>
          </div>
          <div className={`${styles.stepLine} ${currentStep > 2 ? styles.stepLineActive : ""}`} />

          <div
            className={`${styles.stepItem} ${currentStep >= 3 ? styles.stepItemActive : ""}`}
            onClick={() => setCurrentStep(3)}
            role="button"
            tabIndex={0}
          >
            <div
              className={
                currentStep === 3
                  ? styles.stepCircleActive
                  : currentStep > 3
                    ? styles.stepCircleCompleted
                    : styles.stepCircle
              }
            >
              03
            </div>
            <span className={currentStep === 3 ? styles.stepLabelActive : styles.stepLabel}>
              Homestead Exemption
            </span>
          </div>
          <div className={`${styles.stepLine} ${currentStep > 3 ? styles.stepLineActive : ""}`} />

          <div
            className={`${styles.stepItem} ${currentStep >= 4 ? styles.stepItemActive : ""}`}
            onClick={() => setCurrentStep(4)}
            role="button"
            tabIndex={0}
          >
            <div
              className={
                currentStep === 4
                  ? styles.stepCircleActive
                  : currentStep > 4
                    ? styles.stepCircleCompleted
                    : styles.stepCircle
              }
            >
              04
            </div>
            <span className={currentStep === 4 ? styles.stepLabelActive : styles.stepLabel}>
              Your Signature
            </span>
          </div>
        </div>

        {/* STEP 1: RELATIONSHIP TO PROPERTY */}
        {currentStep === 1 && (
          <div>
            <div className={styles.heroSection}>
              <div className={styles.heroLeft}>
                <span className={styles.stepBadge}>Step 01 of 04</span>
                <h1 className={styles.heroTitle}>Relationship to Property</h1>
                <p className={styles.heroSubtitle}>
                  For each property, let us know if you are the owner or a family member
                  representing the owner.
                </p>
              </div>
              <div className={styles.heroRight}>
                <div className={styles.mascotWrap}>
                  <svg className={styles.checkGraphic} viewBox="0 0 120 100" fill="none">
                    <path
                      d="M15 50L45 80L105 20"
                      stroke="#00A86B"
                      strokeWidth="16"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <img
                    src={Images.andyHoldingSign}
                    alt="Andy mascot"
                    className={styles.heroMascot}
                  />
                </div>
              </div>
            </div>

            <div className={styles.propertyCardList}>
              {vm.isAllPropertiesLoading && displayedProperties.length === 0 ? (
                <div style={{ textAlign: "center", padding: "48px 16px", color: "#6b7280" }}>
                  <p style={{ margin: 0, fontSize: "15px", fontWeight: 500 }}>
                    Loading your properties...
                  </p>
                </div>
              ) : (
                displayedProperties.map((property, idx) => {
                  const pid = getPropertyId(property);
                  const type = relationshipTypes[pid] || "owner";
                  const pOwnerRel = ownerRelationships[pid];
                  const pFilingReason = filingReasons[pid] || "";
                  const pFamilyLicenseFileName = familyLicenseFileNames[pid] || "";
                  const pErrors = familyFieldErrors[pid] || {};

                  return (
                    <div key={property.property_id || idx} className={styles.propertyCard}>
                      <div className={styles.propertyCardHeader}>
                        <div className={styles.propertyMeta}>
                          <input
                            type="checkbox"
                            checked={isProtesting(pid)}
                            onChange={(e) => handleToggleProtest(pid, e.target.checked)}
                            className={styles.checkboxInput}
                            style={{
                              marginRight: "16px",
                              width: "20px",
                              height: "20px",
                              cursor: "pointer",
                              accentColor: "#00A86B",
                            }}
                            title="Select to include this property in the protest"
                          />
                          <div className={styles.propertyIconBadge}>
                            <HomeIcon />
                          </div>
                          <div className={styles.propertyAddressCol}>
                            <h4 className={styles.propertyAddress}>
                              {getFormattedAddress(property)}
                            </h4>
                            <span className={styles.propertyCounty}>
                              {getFormattedCounty(property)}
                            </span>
                            {(() => {
                              let savings = property?.amount_saved;
                              if (savings === null || savings === undefined) {
                                const taxAmount = property?.tax_info?.tax_amount;
                                if (taxAmount && taxAmount > 0) {
                                  savings = Math.round(taxAmount * 0.08);
                                } else {
                                  const assessedVal =
                                    property?.tax_info?.assessed_value ??
                                    property?.assessed_value ??
                                    0;
                                  const marketVal =
                                    property?.tax_info?.market_value ??
                                    property?.estimated_value ??
                                    0;
                                  savings =
                                    assessedVal > marketVal && marketVal > 0
                                      ? Math.round((assessedVal - marketVal) * 0.025)
                                      : 0;
                                }
                              }

                              return (
                                <div
                                  style={{
                                    color: "#00A86B",
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    marginTop: "4px",
                                  }}
                                >
                                  Estimated Savings: ${Number(savings).toLocaleString()}
                                </div>
                              );
                            })()}
                          </div>
                        </div>

                        <div className={styles.propertyRightActions}>
                          <div className={styles.relationshipRadioGroup}>
                            <button
                              type="button"
                              className={`${styles.radioOptionBtn} ${
                                type === "owner" ? styles.radioOptionActive : ""
                              }`}
                              onClick={() => handleOwnerSelect(pid)}
                            >
                              <span className={styles.radioIndicator}>
                                {type === "owner" ? <span className={styles.radioDot} /> : null}
                              </span>
                              <span>Owner</span>
                            </button>

                            <button
                              type="button"
                              className={`${styles.radioOptionBtn} ${
                                type === "family" ? styles.radioOptionActive : ""
                              }`}
                              onClick={() => handleFamilySelect(pid)}
                            >
                              <span className={styles.radioIndicator}>
                                {type === "family" ? <span className={styles.radioDot} /> : null}
                              </span>
                              <span>Family Member</span>
                            </button>
                          </div>

                          <button
                            type="button"
                            className={styles.accordionToggleBtn}
                            onClick={() => {
                              if (type === "family") handleOwnerSelect(pid);
                              else handleFamilySelect(pid);
                            }}
                            aria-label="Toggle details"
                          >
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path
                                d={type === "family" ? "M18 15l-6-6-6 6" : "M6 9l6 6 6-6"}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>

                      {type === "family" && (
                        <div className={styles.propertyCardBody}>
                          <div className={styles.familyFieldsGrid}>
                            <div className={styles.familyColLeft}>
                              <label className={styles.fieldTitle}>
                                Relationship to Owner <span className={styles.requiredStar}>*</span>
                              </label>
                              <div
                                className={`${styles.radioOptionsRow} ${
                                  pErrors.ownerRelationship ? styles.radioOptionsRowError : ""
                                }`}
                              >
                                {ownerRelationshipOptions.map((option) => (
                                  <button
                                    key={option}
                                    type="button"
                                    className={`${styles.familyRadioBtn} ${
                                      pOwnerRel === option ? styles.familyRadioActive : ""
                                    }`}
                                    onClick={() => handleOwnerRelationshipSelect(pid, option)}
                                  >
                                    <span className={styles.radioIndicator}>
                                      {pOwnerRel === option ? (
                                        <span className={styles.radioDot} />
                                      ) : null}
                                    </span>
                                    <span>{option}</span>
                                  </button>
                                ))}
                              </div>
                              {pErrors.ownerRelationship && (
                                <div className={styles.errorMessage}>
                                  {pErrors.ownerRelationship}
                                </div>
                              )}
                            </div>

                            <div className={styles.familyColRight}>
                              <div className={styles.fieldTitleGroup}>
                                <label className={styles.fieldTitle}>
                                  Driving License <span className={styles.requiredStar}>*</span>
                                </label>
                                <label
                                  htmlFor={`family-driving-license-front-upload-${pid}`}
                                  className={`${styles.uploadLicenseLink} ${
                                    pErrors.drivingLicenseFront ? styles.uploadLicenseError : ""
                                  }`}
                                >
                                  Upload License
                                </label>
                                <input
                                  id={`family-driving-license-front-upload-${pid}`}
                                  type="file"
                                  className={styles.hiddenFileInput}
                                  accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.webp,image/*"
                                  onChange={(e) => handleFamilyDrivingLicenseChange(pid, e)}
                                />
                              </div>

                              {pFamilyLicenseFileName && (
                                <div className={styles.licenseFileNameBadge}>
                                  📄 {pFamilyLicenseFileName}
                                </div>
                              )}
                              {pErrors.drivingLicenseFront && !familyLicenseFiles[pid] && (
                                <div className={styles.errorMessage}>
                                  {pErrors.drivingLicenseFront}
                                </div>
                              )}

                              <textarea
                                id={`filing-reason-${pid}`}
                                className={`${styles.reasonTextarea} ${
                                  pErrors.filingReason ? styles.inputError : ""
                                }`}
                                value={pFilingReason}
                                onChange={(e) => handleReasonChange(pid, e.target.value)}
                                placeholder="Owner is elderly and has authorized me to manage their filings."
                                rows={3}
                              />
                              {pErrors.filingReason && (
                                <div className={styles.errorMessage}>{pErrors.filingReason}</div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* STEP 2: WHAT KIND OF PROPERTY */}
        {currentStep === 2 && (
          <div>
            <div className={styles.heroSection}>
              <div className={styles.heroLeft}>
                <span className={styles.stepBadge}>Step 02 of 04</span>
                <h1 className={styles.heroTitle}>What Kind of Property</h1>
                <p className={styles.heroSubtitle}>
                  Select the type of property for each property below. This determines the forms
                  we&apos;ll file and the evidence we&apos;ll need.
                </p>
              </div>
              <div className={styles.heroRight}>
                <div className={styles.houseIllustrationWrap}>
                  <svg width="130" height="100" viewBox="0 0 130 100" fill="none">
                    <ellipse cx="65" cy="85" rx="55" ry="14" fill="#E6F4ED" />
                    <circle cx="95" cy="45" r="16" fill="#ECFDF5" />
                    <circle cx="108" cy="42" r="12" fill="#D1FAE5" />
                    <path
                      d="M25 50 L65 18 L105 50"
                      stroke="#00A86B"
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M30 48 L65 20 L100 48 V82 H30 V48 Z"
                      fill="#F0FDF4"
                      stroke="#00A86B"
                      strokeWidth="2.5"
                    />
                    <rect x="80" y="24" width="8" height="16" fill="#00A86B" rx="1" />
                    <rect x="56" y="56" width="18" height="26" fill="#00A86B" rx="3" />
                    <circle cx="60" cy="70" r="1.5" fill="#FFFFFF" />
                    <rect x="38" y="52" width="12" height="12" fill="#34D399" rx="2" />
                    <rect x="80" y="52" width="12" height="12" fill="#34D399" rx="2" />
                    <circle cx="18" cy="62" r="12" fill="#00A86B" />
                    <rect x="16" y="72" width="4" height="12" fill="#065F46" rx="1" />
                    <circle cx="112" cy="64" r="10" fill="#059669" />
                    <rect x="110" y="72" width="4" height="12" fill="#065F46" rx="1" />
                  </svg>
                </div>
              </div>
            </div>

            <div className={styles.propertyCardList}>
              {activeProperties.map((property, idx) => {
                const pid = getPropertyId(property);
                return (
                  <div key={pid || idx} className={styles.propertyCard}>
                    <div className={styles.propertyCardHeader}>
                      <div className={styles.propertyMeta}>
                        <div className={styles.propertyIconBadge}>
                          <HomeIcon />
                        </div>
                        <div className={styles.propertyAddressCol}>
                          <h4 className={styles.propertyAddress}>
                            {getFormattedAddress(property)}
                          </h4>
                          <span className={styles.propertyCounty}>
                            {getFormattedCounty(property)}
                          </span>
                        </div>
                      </div>

                      <div className={styles.propertyRightActions}>
                        <button
                          type="button"
                          className={styles.accordionToggleBtn}
                          onClick={() =>
                            setExpandedStep2((prev) => ({
                              ...prev,
                              [pid]: prev[pid] === false ? true : false,
                            }))
                          }
                          aria-label="Toggle details"
                        >
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path
                              d={expandedStep2[pid] !== false ? "M18 15l-6-6-6 6" : "M6 9l6 6 6-6"}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {expandedStep2[pid] !== false && (
                      <div className={styles.propertyCardBody}>
                        <section
                          className={styles.typeSection}
                          aria-labelledby={`property-type-heading-${pid}`}
                        >
                          <h3
                            className={styles.typeSectionTitle}
                            id={`property-type-heading-${pid}`}
                          >
                            Select Property Type <span aria-hidden="true">*</span>
                          </h3>
                          <div className={styles.typeCardsShell}>
                            <div
                              className={styles.typeRow}
                              role="group"
                              aria-label="Select property type"
                            >
                              <button
                                type="button"
                                className={`${styles.typeCard} ${vm.isSelected(pid, "RESIDENTIAL") ? styles.typeCardActive : ""}`}
                                aria-pressed={vm.isSelected(pid, "RESIDENTIAL")}
                                onClick={() => vm.onSelectPropertyType(pid, "RESIDENTIAL")}
                              >
                                <span className={styles.typeIcon} aria-hidden="true">
                                  <HomeIcon />
                                </span>
                                <span className={styles.typeTextCol}>
                                  <span className={styles.typeLabel}>Residential Property</span>
                                  <span className={styles.typeDescription}>
                                    Single-family home or residential property
                                  </span>
                                </span>
                                <span className={styles.typeChevron} aria-hidden="true">
                                  <ChevronRightIcon />
                                </span>
                              </button>

                              <button
                                type="button"
                                className={`${styles.typeCard} ${vm.isSelected(pid, "COMMERCIAL") ? styles.typeCardActive : ""}`}
                                aria-pressed={vm.isSelected(pid, "COMMERCIAL")}
                                onClick={() => vm.onSelectPropertyType(pid, "COMMERCIAL")}
                              >
                                <span className={styles.typeIcon} aria-hidden="true">
                                  <BuildingIcon />
                                </span>
                                <span className={styles.typeTextCol}>
                                  <span className={styles.typeLabel}>Commercial Property</span>
                                  <span className={styles.typeDescription}>
                                    Commercial, Multifamily, Hotel or any commercial property
                                  </span>
                                </span>
                                <span className={styles.typeChevron} aria-hidden="true">
                                  <ChevronRightIcon />
                                </span>
                              </button>

                              <button
                                type="button"
                                className={`${styles.typeCard} ${vm.isSelected(pid, "LAND_OR_LOT") ? styles.typeCardActive : ""}`}
                                aria-pressed={vm.isSelected(pid, "LAND_OR_LOT")}
                                onClick={() => vm.onSelectPropertyType(pid, "LAND_OR_LOT")}
                              >
                                <span className={styles.typeIcon} aria-hidden="true">
                                  <LandIcon />
                                </span>
                                <span className={styles.typeTextCol}>
                                  <span className={styles.typeLabel}>Land / Lot</span>
                                  <span className={styles.typeDescription}>
                                    Vacant land, residential lot, or acreage
                                  </span>
                                </span>
                                <span className={styles.typeChevron} aria-hidden="true">
                                  <ChevronRightIcon />
                                </span>
                              </button>
                            </div>

                            {vm.isSelected(pid, "RESIDENTIAL") && (
                              <div className={styles.homesteadCheckboxShell}>
                                <label className={styles.homesteadOptionRow}>
                                  <input
                                    type="checkbox"
                                    checked={fileHomesteadExemption[pid] !== false}
                                    onChange={(e) =>
                                      setFileHomesteadExemption((prev) => ({
                                        ...prev,
                                        [pid]: e.target.checked,
                                      }))
                                    }
                                    className={styles.checkboxInput}
                                  />
                                  <span className={styles.homesteadOptionText}>
                                    File Homestead Exemption for this property
                                  </span>
                                </label>
                              </div>
                            )}
                          </div>
                        </section>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* STEP 3: HOMESTEAD EXEMPTION */}
        {currentStep === 3 && (
          <div>
            <div className={styles.heroSection}>
              <div className={styles.heroLeft}>
                <span className={styles.stepBadge}>Step 03 of 04</span>
                <h1 className={styles.heroTitle}>Homestead Exemption</h1>
                <p className={styles.heroSubtitle}>
                  Tell us if you qualify for any homestead exemptions for each property.
                </p>
              </div>
              <div className={styles.heroRight}>
                <div className={styles.shieldIllustrationWrap}>
                  <svg width="130" height="100" viewBox="0 0 130 100" fill="none">
                    <ellipse cx="65" cy="85" rx="55" ry="14" fill="#E6F4ED" />
                    <path
                      d="M20 52 L55 22 L90 52 V82 H20 V52 Z"
                      fill="#F0FDF4"
                      stroke="#00A86B"
                      strokeWidth="2.5"
                    />
                    <path
                      d="M15 52 L55 20 L95 52"
                      stroke="#00A86B"
                      strokeWidth="3.5"
                      strokeLinecap="round"
                    />
                    <rect x="46" y="60" width="18" height="22" fill="#00A86B" rx="2" />
                    <rect x="30" y="54" width="10" height="10" fill="#34D399" rx="2" />
                    <g transform="translate(70, 22)">
                      <path
                        d="M24 0 C24 0 44 6 44 24 C44 42 24 54 24 54 C24 54 4 42 4 24 C4 6 24 0 24 0 Z"
                        fill="#00A86B"
                        stroke="#FFFFFF"
                        strokeWidth="2"
                      />
                      <path
                        d="M14 24 L21 31 L34 17"
                        stroke="#FFFFFF"
                        strokeWidth="3.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </g>
                  </svg>
                </div>
              </div>
            </div>

            <div className={styles.propertyCardList}>
              {activeProperties.filter(
                (p) =>
                  vm.isSelected(getPropertyId(p), "RESIDENTIAL") &&
                  fileHomesteadExemption[getPropertyId(p)] !== false
              ).length === 0 ? (
                <div className={styles.infoNoteBox}>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#00A86B"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                  <span>
                    Homestead exemptions only apply to Residential properties where you have chosen
                    to file for homestead exemption.
                  </span>
                </div>
              ) : (
                activeProperties
                  .filter(
                    (p) =>
                      vm.isSelected(getPropertyId(p), "RESIDENTIAL") &&
                      fileHomesteadExemption[getPropertyId(p)] !== false
                  )
                  .map((property, idx) => {
                    const pid = getPropertyId(property);
                    const resStatus = residentialStatuses[pid] || "moving";
                    const occupancyAnswer = occupancyQuestionAnswers[pid] || "yes";
                    const previousAddress = previousTexasAddresses[pid] || "";
                    const pErrors = familyFieldErrors[pid] || {};

                    const shouldShowConditional =
                      (resStatus === "moving" && occupancyAnswer === "yes") ||
                      (resStatus === "living" && occupancyAnswer === "no");

                    const categoryStatus = getSectionStatus(pid, "category");
                    const occupancyStatus = getSectionStatus(pid, "occupancy");
                    const questionStatus = getSectionStatus(pid, "question");
                    const previousAddressStatus = getSectionStatus(pid, "previousAddress");
                    const documentsStatus = getSectionStatus(pid, "documents");

                    return (
                      <div key={pid || idx} className={styles.propertyCard}>
                        <div className={styles.propertyCardHeader}>
                          <div className={styles.propertyMeta}>
                            <div className={styles.propertyIconBadge}>
                              <HomeIcon />
                            </div>
                            <div className={styles.propertyAddressCol}>
                              <h4 className={styles.propertyAddress}>
                                {getFormattedAddress(property)}
                              </h4>
                              <span className={styles.propertyCounty}>
                                {getFormattedCounty(property)}
                              </span>
                            </div>
                          </div>

                          <div className={styles.propertyRightActions}>
                            <div className={styles.propertyTypeTag} style={{ marginRight: "16px" }}>
                              <span className={styles.propertyTypeTagLabel}>Property Type:</span>
                              <span className={styles.propertyTypeTagVal}>Residential</span>
                            </div>
                            <button
                              type="button"
                              className={styles.accordionToggleBtn}
                              onClick={() =>
                                setExpandedStep3((prev) => ({
                                  ...prev,
                                  [pid]: prev[pid] === false ? true : false,
                                }))
                              }
                              aria-label="Toggle details"
                            >
                              <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <path
                                  d={
                                    expandedStep3[pid] !== false
                                      ? "M18 15l-6-6-6 6"
                                      : "M6 9l6 6 6-6"
                                  }
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>

                        {expandedStep3[pid] !== false && (
                          <div className={styles.propertyCardBody}>
                            <div className={styles.homesteadGrid}>
                              {/* LEFT COLUMN: CATEGORY, OCCUPANCY, QUESTION, PREVIOUS ADDRESS */}
                              <div className={styles.homesteadLeftCol}>
                                {/* HOMESTEAD EXEMPTION CATEGORY */}
                                <div
                                  className={`${styles.fieldBlock} ${styles.categoryBlock} ${styles.sectionBlock} ${
                                    categoryStatus === "faded"
                                      ? styles.sectionBlockFaded
                                      : styles.sectionBlockActive
                                  }`}
                                  onClick={() =>
                                    setFocusedSection((prev) => ({ ...prev, [pid]: "category" }))
                                  }
                                >
                                  <label className={styles.sectionHeaderTitle}>
                                    HOMESTEAD EXEMPTION CATEGORY{" "}
                                    <span className={styles.requiredStar}>*</span>
                                  </label>
                                  <div className={styles.categoryOptionsList}>
                                    <label
                                      className={`${styles.categoryCheckboxCard} ${
                                        isGeneralHomesteads[pid]
                                          ? styles.categoryCheckboxCardActive
                                          : ""
                                      }`}
                                    >
                                      <input
                                        type="radio"
                                        name={`homesteadCategory_${pid}`}
                                        checked={Boolean(isGeneralHomesteads[pid])}
                                        onChange={(e) =>
                                          handleToggleGeneralHomestead(pid, e.target.checked)
                                        }
                                        className={styles.checkboxInput}
                                      />
                                      <span className={styles.categoryCheckboxLabel}>
                                        General Residence Homestead Exemption
                                      </span>
                                    </label>

                                    <label
                                      className={`${styles.categoryCheckboxCard} ${
                                        isOver65s[pid] ? styles.categoryCheckboxCardActive : ""
                                      }`}
                                    >
                                      <input
                                        type="radio"
                                        name={`homesteadCategory_${pid}`}
                                        checked={Boolean(isOver65s[pid])}
                                        onChange={(e) => handleToggleOver65(pid, e.target.checked)}
                                        className={styles.checkboxInput}
                                      />
                                      <span className={styles.categoryCheckboxLabel}>
                                        Person Age 65 or Older (or Surviving Spouse)
                                      </span>
                                    </label>
                                  </div>
                                  {pErrors?.homesteadCategory && (
                                    <p className={styles.errorMessage}>
                                      {pErrors.homesteadCategory}
                                    </p>
                                  )}
                                </div>

                                {/* OCCUPANCY */}
                                <div
                                  className={`${styles.fieldBlock} ${styles.sectionBlock} ${
                                    occupancyStatus === "faded"
                                      ? styles.sectionBlockFaded
                                      : styles.sectionBlockActive
                                  }`}
                                  onClick={() =>
                                    setFocusedSection((prev) => ({ ...prev, [pid]: "occupancy" }))
                                  }
                                >
                                  <label className={styles.sectionHeaderTitle}>
                                    OCCUPANCY <span className={styles.requiredStar}>*</span>
                                  </label>
                                  <div className={styles.occupancyRow}>
                                    <button
                                      type="button"
                                      className={`${styles.occupancyPill} ${
                                        resStatus === "moving" ? styles.occupancyPillActive : ""
                                      }`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleResidentialStatusChange(pid, "moving");
                                        setFocusedSection((prev) => ({
                                          ...prev,
                                          [pid]: "occupancy",
                                        }));
                                      }}
                                    >
                                      <span
                                        className={`${styles.occupancyRadioCircle} ${
                                          resStatus === "moving" ? styles.occupancyRadioActive : ""
                                        }`}
                                      >
                                        {resStatus === "moving" ? (
                                          <span className={styles.occupancyRadioDot} />
                                        ) : null}
                                      </span>
                                      <span className={styles.occupancyLabelText}>
                                        Moving into this home
                                      </span>
                                    </button>

                                    <button
                                      type="button"
                                      className={`${styles.occupancyPill} ${
                                        resStatus === "living" ? styles.occupancyPillActive : ""
                                      }`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleResidentialStatusChange(pid, "living");
                                        setFocusedSection((prev) => ({
                                          ...prev,
                                          [pid]: "occupancy",
                                        }));
                                      }}
                                    >
                                      <span
                                        className={`${styles.occupancyRadioCircle} ${
                                          resStatus === "living" ? styles.occupancyRadioActive : ""
                                        }`}
                                      >
                                        {resStatus === "living" ? (
                                          <span className={styles.occupancyRadioDot} />
                                        ) : null}
                                      </span>
                                      <span className={styles.occupancyLabelText}>
                                        Already living in this home
                                      </span>
                                    </button>
                                  </div>
                                </div>

                                {/* OCCUPANCY QUESTION */}
                                <div
                                  className={`${styles.fieldBlock} ${styles.sectionBlock} ${
                                    questionStatus === "faded"
                                      ? styles.sectionBlockFaded
                                      : styles.sectionBlockActive
                                  }`}
                                  onClick={() =>
                                    setFocusedSection((prev) => ({ ...prev, [pid]: "question" }))
                                  }
                                >
                                  <label className={styles.sectionHeaderTitle}>
                                    {resStatus === "moving"
                                      ? "HAVE YOU OWNED A HOME IN TEXAS BEFORE PURCHASING THIS PROPERTY? *"
                                      : "DOES THE ADDRESS ON YOUR TEXAS DRIVER'S LICENSE MATCH THIS PROPERTY? *"}
                                  </label>
                                  <div className={styles.yesNoRow}>
                                    <button
                                      type="button"
                                      className={`${styles.yesNoPillBtn} ${
                                        occupancyAnswer === "yes" ? styles.yesNoPillBtnActive : ""
                                      }`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOccupancyQuestionAnswerChange(pid, "yes");
                                        setFocusedSection((prev) => ({
                                          ...prev,
                                          [pid]: "question",
                                        }));
                                      }}
                                    >
                                      Yes
                                    </button>
                                    <button
                                      type="button"
                                      className={`${styles.yesNoPillBtn} ${
                                        occupancyAnswer === "no" ? styles.yesNoPillBtnActive : ""
                                      }`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOccupancyQuestionAnswerChange(pid, "no");
                                        setFocusedSection((prev) => ({
                                          ...prev,
                                          [pid]: "question",
                                        }));
                                      }}
                                    >
                                      No
                                    </button>
                                  </div>
                                </div>

                                {/* PREVIOUS TEXAS PROPERTY (CONDITIONAL) */}
                                {shouldShowConditional && (
                                  <div
                                    className={`${styles.fieldBlock} ${styles.sectionBlock} ${
                                      previousAddressStatus === "faded"
                                        ? styles.sectionBlockFaded
                                        : styles.sectionBlockActive
                                    }`}
                                    onClick={() =>
                                      setFocusedSection((prev) => ({
                                        ...prev,
                                        [pid]: "previousAddress",
                                      }))
                                    }
                                  >
                                    <label className={styles.sectionHeaderTitle}>
                                      ENTER YOUR PREVIOUS TEXAS PROPERTY{" "}
                                      <span className={styles.requiredStar}>*</span>
                                    </label>

                                    {previousAddress && !isEditingPreviousAddress[pid] ? (
                                      <div className={styles.previousAddressCard}>
                                        <div className={styles.prevAddressIconWrap}>
                                          <HomeIcon />
                                        </div>
                                        <span className={styles.prevAddressText}>
                                          {previousAddress}
                                        </span>
                                        <button
                                          type="button"
                                          className={styles.changeAddressBtn}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setIsEditingPreviousAddress((prev) => ({
                                              ...prev,
                                              [pid]: true,
                                            }));
                                            setFocusedSection((prev) => ({
                                              ...prev,
                                              [pid]: "previousAddress",
                                            }));
                                          }}
                                        >
                                          Change
                                        </button>
                                      </div>
                                    ) : (
                                      <div className={styles.prevAddressInputContainer}>
                                        <div className={styles.prevAddressInputRow}>
                                          <div className={styles.prevAddressInputIcon}>
                                            <HomeIcon />
                                          </div>
                                          <input
                                            type="text"
                                            className={styles.prevAddressInput}
                                            placeholder="Enter previous Texas property address..."
                                            value={addressInputValues[pid] ?? previousAddress}
                                            onFocus={() =>
                                              setFocusedSection((prev) => ({
                                                ...prev,
                                                [pid]: "previousAddress",
                                              }))
                                            }
                                            onChange={(e) =>
                                              handleAddressInputChange(pid, e.target.value)
                                            }
                                            onKeyDown={(e) => {
                                              if (e.key === "Enter") {
                                                e.preventDefault();
                                                handleSaveAddress(pid);
                                              }
                                            }}
                                          />
                                          <button
                                            type="button"
                                            className={styles.saveAddressBtn}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleSaveAddress(pid);
                                            }}
                                          >
                                            Done
                                          </button>
                                        </div>
                                        {Boolean(addressSuggestions[pid]?.length) && (
                                          <div className={styles.suggestionsDropdown}>
                                            {(addressSuggestions[pid] || []).map((sug, sIdx) => {
                                              const text =
                                                sug.address ||
                                                `${sug.house || ""} ${sug.street || ""}, ${sug.city || ""}, ${sug.state || ""} ${sug.zip || ""}`.trim();
                                              return (
                                                <div
                                                  key={sIdx}
                                                  className={styles.suggestionItem}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleSaveAddress(pid, text);
                                                  }}
                                                >
                                                  {text}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                    {pErrors?.previousAddress && (
                                      <p className={styles.errorMessage}>
                                        {pErrors.previousAddress}
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>

                              {/* RIGHT COLUMN: DRIVING LICENSE, NOTE, DEED / CLOSING DOCS */}
                              <div
                                className={`${styles.homesteadRightCol} ${styles.sectionBlock} ${
                                  documentsStatus === "faded"
                                    ? styles.sectionBlockFaded
                                    : styles.sectionBlockActive
                                }`}
                                onClick={() =>
                                  setFocusedSection((prev) => ({ ...prev, [pid]: "documents" }))
                                }
                              >
                                <div className={styles.fieldBlock}>
                                  <label className={styles.sectionHeaderTitle}>
                                    DRIVING LICENSE{" "}
                                    <span className={styles.optionalBadge}>Optional</span>
                                  </label>

                                  {/* Front of Driving License */}
                                  <div
                                    className={styles.documentUploadCard}
                                    onClick={() =>
                                      document.getElementById(`homestead-dl-front-${pid}`)?.click()
                                    }
                                    role="button"
                                    tabIndex={0}
                                  >
                                    <div className={styles.docUploadThumb}>
                                      {drivingLicenseFrontUrls[pid] ? (
                                        <img
                                          src={drivingLicenseFrontUrls[pid]}
                                          alt="Front of License"
                                          className={styles.thumbImage}
                                        />
                                      ) : (
                                        <svg
                                          width="24"
                                          height="24"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="#94A3B8"
                                          strokeWidth="1.5"
                                        >
                                          <rect x="3" y="4" width="18" height="16" rx="3" />
                                          <circle cx="9" cy="10" r="2" />
                                          <line x1="15" y1="9" x2="17" y2="9" />
                                          <line x1="15" y1="12" x2="17" y2="12" />
                                          <line x1="7" y1="16" x2="17" y2="16" />
                                        </svg>
                                      )}
                                    </div>
                                    <div className={styles.docUploadMeta}>
                                      <span className={styles.docUploadTitle}>
                                        Front of Driving License
                                      </span>
                                      <span className={styles.docUploadSubtitle}>
                                        {drivingLicenseFrontFiles[pid]
                                          ? drivingLicenseFrontFileNames[pid] ||
                                            "Attached · tap to replace"
                                          : "Tap to upload"}
                                      </span>
                                    </div>
                                    <div className={styles.docUploadStatus}>
                                      {drivingLicenseFrontFiles[pid] ? (
                                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                                          <circle cx="12" cy="12" r="11" fill="#00A86B" />
                                          <path
                                            d="M7 12.5l3.5 3.5 7-7"
                                            stroke="#FFFFFF"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                        </svg>
                                      ) : (
                                        <span className={styles.uploadPlusIcon}>+</span>
                                      )}
                                    </div>
                                    <input
                                      id={`homestead-dl-front-${pid}`}
                                      type="file"
                                      accept="image/*,application/pdf"
                                      className={styles.hiddenFileInput}
                                      onChange={(e) => handleDrivingLicenseFrontChange(pid, e)}
                                    />
                                  </div>

                                  {/* Back of Driving License */}
                                  <div
                                    className={styles.documentUploadCard}
                                    style={{ marginTop: "10px" }}
                                    onClick={() =>
                                      document.getElementById(`homestead-dl-back-${pid}`)?.click()
                                    }
                                    role="button"
                                    tabIndex={0}
                                  >
                                    <div className={styles.docUploadThumb}>
                                      {drivingLicenseBackUrls[pid] ? (
                                        <img
                                          src={drivingLicenseBackUrls[pid]}
                                          alt="Back of License"
                                          className={styles.thumbImage}
                                        />
                                      ) : (
                                        <svg
                                          width="24"
                                          height="24"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="#94A3B8"
                                          strokeWidth="1.5"
                                        >
                                          <rect x="3" y="4" width="18" height="16" rx="3" />
                                          <circle cx="9" cy="10" r="2" />
                                          <line x1="15" y1="9" x2="17" y2="9" />
                                          <line x1="15" y1="12" x2="17" y2="12" />
                                          <line x1="7" y1="16" x2="17" y2="16" />
                                        </svg>
                                      )}
                                    </div>
                                    <div className={styles.docUploadMeta}>
                                      <span className={styles.docUploadTitle}>
                                        Back of Driving License
                                      </span>
                                      <span className={styles.docUploadSubtitle}>
                                        {drivingLicenseBackFiles[pid]
                                          ? drivingLicenseBackFileNames[pid] ||
                                            "Attached · tap to replace"
                                          : "Tap to upload"}
                                      </span>
                                    </div>
                                    <div className={styles.docUploadStatus}>
                                      {drivingLicenseBackFiles[pid] ? (
                                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                                          <circle cx="12" cy="12" r="11" fill="#00A86B" />
                                          <path
                                            d="M7 12.5l3.5 3.5 7-7"
                                            stroke="#FFFFFF"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                        </svg>
                                      ) : (
                                        <span className={styles.uploadPlusIcon}>+</span>
                                      )}
                                    </div>
                                    <input
                                      id={`homestead-dl-back-${pid}`}
                                      type="file"
                                      accept="image/*,application/pdf"
                                      className={styles.hiddenFileInput}
                                      onChange={(e) => handleDrivingLicenseBackChange(pid, e)}
                                    />
                                  </div>

                                  {/* Note Box */}
                                  <div className={styles.homesteadNoteBox}>
                                    <div className={styles.homesteadNoteIcon}>
                                      <svg
                                        width="18"
                                        height="18"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="#00A86B"
                                        strokeWidth="2.2"
                                      >
                                        <circle cx="12" cy="12" r="10" />
                                        <line
                                          x1="12"
                                          y1="16"
                                          x2="12"
                                          y2="12"
                                          strokeLinecap="round"
                                        />
                                        <line
                                          x1="12"
                                          y1="8"
                                          x2="12.01"
                                          y2="8"
                                          strokeLinecap="round"
                                        />
                                      </svg>
                                    </div>
                                    <p className={styles.homesteadNoteText}>
                                      <strong>Note:</strong> Uploading your driver&apos;s license
                                      and closing documents is optional right now. You can upload or
                                      update them anytime later in Exemption History after filing.
                                    </p>
                                  </div>
                                </div>

                                {/* DEED / CLOSING DOCUMENTS (CONDITIONAL) */}
                                {shouldShowConditional && (
                                  <div className={styles.deedSection}>
                                    <label className={styles.sectionHeaderTitle}>
                                      DEED / CLOSING DOCUMENTS{" "}
                                      <span className={styles.optionalBadge}>Optional</span>
                                    </label>
                                    <div
                                      className={styles.documentUploadCard}
                                      onClick={() =>
                                        document.getElementById(`homestead-deed-${pid}`)?.click()
                                      }
                                      role="button"
                                      tabIndex={0}
                                    >
                                      <div className={styles.docUploadThumb}>
                                        {deedClosingUrls[pid] ? (
                                          <img
                                            src={deedClosingUrls[pid]}
                                            alt="Deed / Closing Documents"
                                            className={styles.thumbImage}
                                          />
                                        ) : (
                                          <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="#94A3B8"
                                            strokeWidth="1.5"
                                          >
                                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                            <polyline points="14 2 14 8 20 8" />
                                            <line x1="9" y1="13" x2="15" y2="13" />
                                            <line x1="9" y1="17" x2="15" y2="17" />
                                          </svg>
                                        )}
                                      </div>
                                      <div className={styles.docUploadMeta}>
                                        <span className={styles.docUploadTitle}>
                                          Deed / Closing Documents
                                        </span>
                                        <span className={styles.docUploadSubtitle}>
                                          {deedClosingFiles[pid]
                                            ? deedClosingFileNames[pid] ||
                                              "Attached · tap to replace"
                                            : "Tap to upload"}
                                        </span>
                                      </div>
                                      <div className={styles.docUploadStatus}>
                                        {deedClosingFiles[pid] ? (
                                          <svg
                                            width="22"
                                            height="22"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                          >
                                            <circle cx="12" cy="12" r="11" fill="#00A86B" />
                                            <path
                                              d="M7 12.5l3.5 3.5 7-7"
                                              stroke="#FFFFFF"
                                              strokeWidth="2.5"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            />
                                          </svg>
                                        ) : (
                                          <span className={styles.uploadPlusIcon}>+</span>
                                        )}
                                      </div>
                                      <input
                                        id={`homestead-deed-${pid}`}
                                        type="file"
                                        accept="image/*,application/pdf"
                                        className={styles.hiddenFileInput}
                                        onChange={(e) => handleDeedClosingChange(pid, e)}
                                      />
                                    </div>
                                    <p className={styles.deedCaption}>
                                      The deed or closing disclosure for this home — a photo or PDF.
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
              )}
            </div>

            {/* <div className={styles.infoNoteBox}>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#00A86B"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
              <span>Homestead exemptions vary by location. You can update this later.</span>
            </div> */}
          </div>
        )}

        {/* STEP 4: YOUR SIGNATURE & DOCUMENTS TO SIGN */}
        {currentStep === 4 && (
          <div>
            <div className={styles.heroSection}>
              <div className={styles.heroLeft}>
                <span className={styles.stepBadge}>Step 04 of 04</span>
                <h1 className={styles.heroTitle}>Documents to Sign & Your Signature</h1>
                <p className={styles.heroSubtitle}>
                  Review the documents and sign below to authorize The Andy App to represent you in
                  the property tax protest process.
                </p>
              </div>
              <div className={styles.heroRight}>
                <div className={styles.penIllustrationWrap}>
                  <svg width="120" height="90" viewBox="0 0 120 90" fill="none">
                    <ellipse cx="60" cy="72" rx="45" ry="12" fill="#E6F4ED" />
                    <path
                      d="M20 62 C30 52, 38 68, 50 58 C58 50, 64 66, 75 58 C82 52, 92 62, 102 55"
                      stroke="#0f172a"
                      strokeWidth="3.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      opacity="0.85"
                    />
                    <g transform="translate(45, 10) rotate(35)">
                      <rect x="18" y="0" width="10" height="38" rx="5" fill="#00A86B" />
                      <rect
                        x="20"
                        y="2"
                        width="6"
                        height="34"
                        rx="3"
                        fill="#34D399"
                        opacity="0.6"
                      />
                      <rect x="18" y="34" width="10" height="4" fill="#F59E0B" />
                      <rect x="19" y="38" width="8" height="16" fill="#1E293B" rx="1" />
                      <path d="M19 54 L23 68 L27 54 Z" fill="#F59E0B" />
                      <path d="M23 54 L23 64" stroke="#78350F" strokeWidth="1" />
                    </g>
                  </svg>
                </div>
              </div>
            </div>

            <div className={styles.step4Grid}>
              {/* Documents to Sign Left Card */}
              <div className={styles.step4Card}>
                <h3 className={styles.step4CardTitle}>Documents to Sign</h3>
                <p className={styles.step4CardSubtitle}>
                  Your signature will be used to complete the following required documents:
                </p>
                <div className={styles.docsListContainer}>
                  {vm.docs.map((doc) => (
                    <div key={doc.id} className={styles.docItemRow}>
                      <div className={styles.docItemLeft}>
                        <div className={styles.docIconBoxGreen}>
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#FFFFFF"
                            strokeWidth="2"
                          >
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                            <line x1="9" y1="15" x2="15" y2="15" />
                          </svg>
                        </div>
                        <div className={styles.docItemMeta}>
                          <span className={styles.docItemTitle}>{doc.title}</span>
                          <span className={styles.docItemDesc}>
                            {doc.desc || "Authorizes The Andy App to represent you on your behalf."}
                          </span>
                        </div>
                      </div>
                      {/* <button
                        type="button"
                        className={styles.previewBtn}
                        onClick={() => vm.onOpenDoc(doc)}
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                        <span>Preview</span>
                      </button> */}
                    </div>
                  ))}
                </div>

                <div className={styles.infoNoteBoxSmall}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#00A86B"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                  <span>
                    We can prefill these documents with your information to save you time.
                  </span>
                </div>
              </div>

              {/* Your Signature Right Card */}
              <div className={styles.step4Card}>
                <h3 className={styles.step4CardTitle}>Your Signature</h3>
                <p className={styles.step4CardSubtitle}>
                  Please sign below to authorize The Andy App to represent you in the property tax
                  protest process.
                </p>

                <div className={styles.signaturePad}>
                  <div className={styles.signaturePadText}>
                    <canvas
                      ref={sigVm.signatureCanvasRef}
                      className={styles.signatureCanvas}
                      onMouseDown={sigVm.startDrawing}
                      onMouseMove={sigVm.draw}
                      onMouseUp={sigVm.stopDrawing}
                      onMouseLeave={sigVm.stopDrawing}
                      onTouchStart={sigVm.startDrawing}
                      onTouchMove={sigVm.draw}
                      onTouchEnd={sigVm.stopDrawing}
                    />
                  </div>
                  <button
                    type="button"
                    className={styles.clearBtn}
                    onClick={sigVm.clearSignature}
                    disabled={!sigVm.signature}
                    aria-disabled={!sigVm.signature}
                  >
                    Clear
                  </button>
                </div>

                <label className={styles.sigAgreementLabel} htmlFor="signature-consent-checkbox">
                  <input
                    id="signature-consent-checkbox"
                    type="checkbox"
                    checked={sigVm.signatureConsentAccepted}
                    onChange={(event) => sigVm.setSignatureConsentAccepted(event.target.checked)}
                    className={styles.checkboxInput}
                  />
                  <span>
                    I agree to the{" "}
                    <a
                      href="#agreement"
                      onClick={(e) => e.preventDefault()}
                      className={styles.greenLink}
                    >
                      Agreement
                    </a>{" "}
                    and authorize The Andy App to represent me in the property tax protest process.
                  </span>
                </label>

                {step4Error && (
                  <div
                    className={styles.errorMessage}
                    style={{ color: "#b42318", fontSize: "14px", marginTop: "8px" }}
                  >
                    {step4Error}
                  </div>
                )}

                {/* <div className={styles.payIfWinBadge}>
                  <span className={styles.starIcon}>⭐</span>
                  <span>You only pay if we win</span>
                </div> */}
              </div>
            </div>
          </div>
        )}

        {/* Signed Preview Section */}
        {/* {vm.hasSignedDocs ? (
          <div className={styles.signedShell}>
            <div className={styles.signedCard}>
              <div className={styles.signedHeader}>
                <div className={styles.signedHeaderLeft}>
                  <div className={styles.signedTitle}>Selected Property</div>
                  <div className={styles.signedAddress}>
                    {vm.propertyDetails?.address?.full_address ?? "—"}
                  </div>
                </div>
              </div>

              <div className={styles.signedTypeRow}>
                <span className={styles.signedTypeIcon} aria-hidden="true">
                  <HomeIcon />
                </span>
                <span className={styles.signedTypeText}>
                  {(
                    vm.propertyDetails?.property_type ||
                    vm.propertyDetails?.property_raw_type ||
                    vm.selectedTypeDisplay ||
                    ""
                  )
                    .toString()
                    .toLowerCase()
                    .includes("commercial")
                    ? "Commercial Property"
                    : "Residential Property"}
                </span>
              </div>

              <div className={styles.signedDivider} />

              <div className={styles.signedDocsTitle}>Documents Preview</div>
              <div className={styles.signedDocsList}>
                {vm.signedDocs.map((doc) => (
                  <button
                    key={doc.id}
                    type="button"
                    className={styles.signedDocRow}
                    disabled={!doc.iframeSrc}
                    aria-disabled={!doc.iframeSrc}
                    onClick={() => vm.onOpenDoc(doc)}
                  >
                    <span className={styles.signedDocCheck} aria-hidden="true">
                      {doc.isComplete ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M9.2 16.2 5.6 12.6l-1.4 1.4 5 5L20.2 8l-1.4-1.4-9.6 9.6Z" fill="currentColor" />
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="2" />
                        </svg>
                      )}
                    </span>
                    <span className={styles.signedDocLabel}>{doc.title}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : null} */}

        {/* Bottom Footer Bar */}
        <div className={styles.footerRow}>
          <div className={styles.footerLeft}>
            {currentStep > 1 && (
              <button type="button" className={styles.backBtn} onClick={handleStepBack}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span>Back</span>
              </button>
            )}
            <span className={styles.propertyCountText}>
              {activeProperties.length > 0
                ? activeProperties.length
                : displayedProperties.length > 0
                  ? displayedProperties.length
                  : 1}{" "}
              {(activeProperties.length || displayedProperties.length) === 1
                ? "property"
                : "properties"}
            </span>
          </div>

          <button
            type="button"
            className={styles.continueBtn}
            onClick={handleStepNext}
            disabled={
              (currentStep === 1 && activeProperties.length === 0) ||
              (currentStep === 4 && sigVm.isSubmitting)
            }
          >
            <span>
              {currentStep === 4
                ? sigVm.isSubmitting
                  ? "Submitting..."
                  : "Review & Submit"
                : "Continue"}
            </span>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>

      {vm.activeDoc && Object.keys(vm.selectedPropertyTypes).length > 0 && (
        <Modal
          isOpen
          onClose={vm.onCloseDoc}
          unconstrained
          className={styles.docModalContent}
          headerClassName={styles.docModalHeader}
          title={
            <>
              <div className={styles.docModalTitle}>{vm.activeDoc.title}</div>
              <div className={styles.docModalSubtitle}>Document Preview</div>
            </>
          }
          titleClassName={styles.docModalTitleGroup}
          bodyClassName={styles.docModalBody}
          ariaLabel="Document preview"
        >
          <iframe
            title={`${vm.activeDoc.title} PDF`}
            src={vm.iframeSrc}
            className={styles.docFrame}
            loading="lazy"
          />
        </Modal>
      )}

      <SignatureModal
        isOpen={vm.isSignatureModalOpen}
        selectedPropertyTypes={vm.selectedPropertyTypes}
        propertyRelationships={propertyRelationships}
        paidPropertyIds={vm.paidPropertyIds}
        onClose={vm.onCloseSignatureModal}
        onSubmit={handleSignatureModalSubmit}
      />

      {vm.isWearAndTearStepperOpen && (
        <Modal
          isOpen={vm.isWearAndTearStepperOpen}
          onClose={vm.onCloseStepper}
          showCloseButton={false}
          closeOnBackdropClick={false}
          closeOnEscape={false}
          unconstrained
          className={styles.stepperContent}
          bodyClassName={styles.stepperModalBody}
          ariaLabel="Property condition stepper"
        >
          <PropertyStepperScreen
            propertyType={
              vm.selectedPropertyTypes[vm.selectedPropertyId] === "COMMERCIAL"
                ? "COMMERCIAL"
                : "RESIDENTIAL"
            }
            propertyId={vm.selectedPropertyId}
            userId={vm.userId}
            currentStep={vm.wearAndTearCurrentStep}
            title={vm.currentStepTitle}
            subtitle={
              vm.currentStepTitle === "Review"
                ? "Please review the uploaded supporting documents before submitting."
                : undefined
            }
            onBack={vm.onStepperBack}
            onClose={() => navigate("/home")}
            onNext={vm.onStepperNext}
            onSkip={vm.onStepperSkip}
            onSubmit={vm.onStepperSubmit}
            residentialSteps={vm.residentialSteps}
            commercialSteps={vm.commercialSteps}
            lastStepHasAnySupportingDocuments={vm.lastStepHasAnySupportingDocuments}
          >
            <ProtestStepperContent
              isResidential={vm.isResidential}
              currentStep={vm.wearAndTearCurrentStep}
              residentialSteps={vm.residentialSteps}
              commercialSteps={vm.commercialSteps}
              localSupportingDocuments={vm.localSupportingDocuments}
            />
          </PropertyStepperScreen>
        </Modal>
      )}

      <AddSupportingDocumentsModalPage
        isOpen={vm.isSupportingDocsModalOpen}
        propertyType={
          vm.selectedPropertyTypes[vm.selectedPropertyId] === "COMMERCIAL"
            ? "COMMERCIAL"
            : "RESIDENTIAL"
        }
        selectedPropertyId={vm.selectedPropertyId}
        onCancel={vm.onCloseSupportingDocsModal}
        onConfirm={vm.onCloseSupportingDocsModal}
      />

      <ProtestSuccessModal
        isOpen={sigVm.isSuccessModalOpen}
        onContinue={sigVm.onSuccessContinue}
        redirectSeconds={sigVm.successRedirectSeconds}
      />

      <ProtestRegistrationModal
        isOpen={isRegistrationModalOpen}
        onClose={() => setIsRegistrationModalOpen(false)}
        initialEmail={guestCustomerEmail}
        onSuccess={handleRegistrationSuccess}
      />
    </section>
  );
};
